import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { HistoryIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getEnrollmentActions } from '../../../../api/enrollments/get-enrollment-history'
import { sendEnrollmentToCse } from '../../../../api/enrollments/send-enrollment-to-cse'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import {
  CseStatusEnum,
  cseStatusEnumMapping
} from '../../../../lib/types/enums/enrollment/cse-status.enum'
import { medicalStatusEnumMapping } from '../../../../lib/types/enums/enrollment/medical-status.enum'
import { PaymentStatusEnumMapping } from '../../../../lib/types/enums/enrollment/payment-status.enum'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import DateChip from '../../../elements/date-chip'
import { EnrollmentActions } from '../enrollment-actions'

type Props = {
  enrollment: Pick<
    Enrollment,
    'id' | 'paymentStatus' | 'medicalStatus' | 'cseStatus' | 'createdAt' | 'updatedAt'
  >
  mode: 'detailed' | 'normal'
}

const EnrollmentStatusOverview = ({ enrollment, mode = 'detailed' }: Props) => {
  const { notify } = useObserver()
  const { paymentStatus, medicalStatus, cseStatus, createdAt, updatedAt } = enrollment
  const { modalState, toggleModal, closeModel } = useModalManager<'enrollment-history'>([
    'enrollment-history'
  ])

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['send-to-cse', enrollment.id],
    onError: showAxiosError,
    mutationFn: sendEnrollmentToCse,
    onSuccess: async () => {
      await notify(['enrollment-profile', 'enrollment-form-update', 'enrollment-status'])
      toast.success('La demande a été envoyée au CSE avec succès')
    }
  })

  const { data: enrollmentActionsData } = useQuery({
    queryKey: ['enrollment-actions', enrollment.id],
    queryFn: () => getEnrollmentActions(enrollment.id)
  })

  return (
    <InnerBox className={'rounded-2xl flex flex-col gap-3'}>
      <div className={'flex flex-row gap-2 items-center justify-between'}>
        <div className={'flex flex-row gap-2 items-center'}>
          <Typography variant={'sub-heading'}>Statuts</Typography>
          <ActionButton
            variant={'lightPurple'}
            icon={HistoryIcon}
            onClick={() => toggleModal('enrollment-history')}
            tooltipText={'Historique des actions'}
          />
        </div>

        {mode === 'detailed' && (
          <div className={'flex items-center gap-2'}>
            <DateChip date={createdAt} label={'Créé'} />
            <DateChip date={updatedAt} label={'Mise à jour'} />
          </div>
        )}
      </div>

      <div className={'flex flex-row gap-2 items-center justify-between'}>
        <div className={'flex flex-row gap-2'}>
          <div className={'flex flex-col gap-1'}>
            <Typography variant={'label-semibold'}>Statut du paiement</Typography>
            <Badge
              size="medium"
              variant={PaymentStatusEnumMapping[paymentStatus].variant}
              text={PaymentStatusEnumMapping[paymentStatus].label}
            />
          </div>

          <div className={'flex flex-col gap-1'}>
            <Typography variant={'label-semibold'}>Statut médical</Typography>
            <Badge
              size="medium"
              variant={medicalStatusEnumMapping[medicalStatus].variant}
              text={medicalStatusEnumMapping[medicalStatus].label}
            />
          </div>

          {cseStatus !== CseStatusEnum.NA && (
            <>
              <div className={'flex flex-col gap-1'}>
                <Typography variant={'label-semibold'}>Statut CSE</Typography>
                <Badge
                  size="medium"
                  variant={cseStatusEnumMapping[cseStatus].variant}
                  text={cseStatusEnumMapping[cseStatus].label}
                />
              </div>

              {![CseStatusEnum.REJECTED, CseStatusEnum.VALIDATED].includes(cseStatus) && (
                <div className={'flex flex-col gap-1'}>
                  <Typography variant={'label-semibold'}>Validation CSE</Typography>
                  <div className={'flex flex-row gap-1'}>
                    {cseStatus === CseStatusEnum.PENDING && (
                      <Badge variant="blue" text="En attente de validation" size="medium" />
                    )}

                    <Button
                      variant="outline"
                      label="Envoyer la demande au CSE"
                      size="small"
                      loading={isPending}
                      onClick={async () => await mutateAsync(enrollment.id)}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* history modal */}
      <Modal
        size={'medium'}
        icon={HistoryIcon}
        title={'Historique des actions'}
        isOpen={modalState['enrollment-history']}
        onCancel={() => closeModel('enrollment-history')}
      >
        <EnrollmentActions data={enrollmentActionsData?.data} />
      </Modal>
    </InnerBox>
  )
}

export default EnrollmentStatusOverview
